import { SyntheticEvent, useState } from 'react';

import NextImage, { ImageLoaderProps } from 'next/image';

import { LoadingSpinner } from '../../../../LoadingSpinner/LoadingSpinner';
import type { Image } from '../../../types/Image';
import { twMerge } from 'tailwind-merge';
import { getImgProxyLoader } from '../../../../../helpers/imageLoaders/imgProxy';

interface PostImageProps {
  userNickname: string | undefined;
  image: Image;
  className?: string;
  useImageDirection?: boolean;
}

export const PostImage = ({
  userNickname,
  image,
  className,
  useImageDirection = false,
}: PostImageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [imageDirection, setImageDirection] = useState<'portrait' | 'landscape'>('portrait');

  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, image.width, 0, { quality });

  const handleLoadingComplete = (event: SyntheticEvent<HTMLImageElement>): void => {
    if (useImageDirection) {
      const img = event.currentTarget;
      if (img.naturalWidth > img.naturalHeight) {
        setImageDirection('landscape');
      } else {
        setImageDirection('portrait');
      }
    }
    setIsLoading(false);
  };

  return (
    <section
      className={twMerge(
        `relative h-full ${useImageDirection && imageDirection === 'portrait' ? 'w-[85%]' : 'w-full'}`,
        className,
      )}
    >
      {isLoading && (
        <div className="absolute flex size-full items-center justify-center">
          <LoadingSpinner className="text-white" />
        </div>
      )}
      <NextImage
        alt={image.alt ?? `Post image from ${userNickname}`}
        className={`size-full ${
          useImageDirection && imageDirection === 'landscape' ? 'object-contain' : 'object-cover'
        } object-center`}
        fill
        onLoad={handleLoadingComplete}
        src={image.url}
        loader={imgProxyLoader}
        sizes="100vw" // todo
      />
    </section>
  );
};
